import { useState, useEffect, useContext, useRef } from "react";
import Preloader from "../../../shared/PreLoaders";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Authorization } from "../../../Context/Authorization";
import { AuthContext } from "../../../Context/AuthContext";

const fmtDate = (val) => (val ? new Date(val).toLocaleDateString("en-IN") : "—");

/* ── Compact table with search + export ── */
const StatusTable = ({ rows, columns, title }) => {
  const [search, setSearch] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const exportRef = useRef(null);

  /* close export dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* filtered rows */
  const filtered = search
    ? rows.filter((row) =>
      columns.some((col) => {
        const raw = col.render ? null : (row[col.key] ?? "");
        return String(raw).toLowerCase().includes(search.toLowerCase()) ||
          String(row.vfcName ?? "").toLowerCase().includes(search.toLowerCase());
      })
    )
    : rows;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  /* reset to page 1 when search or pageSize changes */
  useEffect(() => { setPage(1); }, [search, pageSize]);

  /* plain text value for export */
  const textVal = (col, row) => {
    if (col.key === "_idx") return row._idx;
    if (col.key === "lastMeetingHeld") return fmtDate(row.lastMeetingHeld);
    if (col.key === "lastAuditDate") return fmtDate(row.lastAuditDate);
    if (col.key === "lastRenewalDate") return fmtDate(row.lastRenewalDate);
    if (col.key === "status") return row.lastMeetingHeld ? "Active" : "Inactive";
    return row[col.key] ?? "—";
  };

  const exportExcel = () => {
    const data = filtered.map((row) => {
      const obj = {};
      columns.forEach((col) => { obj[col.label] = textVal(col, row); });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title || "Data");
    XLSX.writeFile(wb, `${title || "export"}.xlsx`);
    setExportOpen(false);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFontSize(11);
    doc.text(title || "Export", 40, 30);
    doc.autoTable({
      head: [columns.map((c) => c.label)],
      body: filtered.map((row) => columns.map((col) => String(textVal(col, row)))),
      startY: 44,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [27, 94, 32], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 251, 245] },
      margin: { left: 20, right: 20 },
    });
    doc.save(`${title || "export"}.pdf`);
    setExportOpen(false);
  };

  const handlePrint = () => {
    const headers = columns.map((c) => `<th style="background:#1B5E20;color:#fff;padding:6px 10px;font-size:11px">${c.label}</th>`).join("");
    const bodyRows = filtered.map((row, i) => {
      const cells = columns.map((col) => `<td style="padding:5px 10px;font-size:11px;border-bottom:1px solid #eee">${textVal(col, row)}</td>`).join("");
      return `<tr style="background:${i % 2 === 0 ? "#fff" : "#F1F8F1"}">${cells}</tr>`;
    }).join("");
    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html><html><head><title>${title || "Print"}</title>
      <style>body{font-family:Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse}@media print{@page{margin:10mm}}</style>
      </head><body>
      <h3 style="color:#1B5E20;margin-bottom:12px">${title || "Data"}</h3>
      <table><thead><tr>${headers}</tr></thead><tbody>${bodyRows}</tbody></table>
      </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 300);
    setExportOpen(false);
  };

  return (
    <div style={{ padding: "10px 14px 12px" }}>

      {/* ── Toolbar ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>

        {/* Search */}
        <div style={{ position: "relative", flex: 1 }}>
          <i className="mdi mdi-magnify" style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#A0AEC0", fontSize: 14, pointerEvents: "none" }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            style={{ width: "100%", padding: "5px 28px 5px 26px", border: "1.5px solid #E2E8F0", borderRadius: 7, fontSize: 12, color: "#1A202C", background: "#fff", outline: "none" }}
            onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
            onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#A0AEC0", fontSize: 13, padding: 0, lineHeight: 1 }}>
              <i className="mdi mdi-close-circle" />
            </button>
          )}
        </div>

        {/* Record count */}
        <span style={{ fontSize: 11, color: "#718096", whiteSpace: "nowrap" }}>
          {filtered.length}{search ? ` / ${rows.length}` : ""} records
        </span>

        {/* Export dropdown */}
        <div ref={exportRef} style={{ position: "relative" }}>
          <button
            onClick={() => setExportOpen((o) => !o)}
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", border: "1.5px solid #E2E8F0", borderRadius: 7, background: exportOpen ? "#E8F5E9" : "#fff", color: exportOpen ? "#1B5E20" : "#4A5568", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
          >
            <i className="mdi mdi-export-variant" style={{ fontSize: 14 }} />
            Export
            <i className={`mdi mdi-chevron-${exportOpen ? "up" : "down"}`} style={{ fontSize: 12, opacity: 0.6 }} />
          </button>

          {exportOpen && (
            <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 9999, background: "#fff", borderRadius: 9, boxShadow: "0 8px 28px rgba(0,0,0,0.13)", border: "1px solid #E2E8F0", minWidth: 160, overflow: "hidden" }}>
              <div style={{ padding: "6px 12px 3px", fontSize: 10, fontWeight: 700, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.5px" }}>Export as</div>
              {[
                { icon: "mdi-microsoft-excel", color: "#217346", label: "Excel (.xlsx)", action: exportExcel, hoverBg: "#F0FFF4" },
                { icon: "mdi-file-pdf-box", color: "#D32F2F", label: "PDF (.pdf)", action: exportPDF, hoverBg: "#FFF5F5" },
                { icon: "mdi-printer", color: "#1565C0", label: "Print", action: handlePrint, hoverBg: "#EBF8FF" },
              ].map((item) => (
                <button
                  key={item.label}
                  onClick={item.action}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 14px", border: "none", cursor: "pointer", width: "100%", textAlign: "left", fontSize: 12, background: "#fff", fontWeight: 500, color: "#2D3748" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = item.hoverBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                >
                  <i className={`mdi ${item.icon}`} style={{ fontSize: 15, color: item.color }} />
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} style={{ padding: "7px 12px", textAlign: "left", fontWeight: 600, fontSize: 11, color: "#4A5568", borderBottom: "1px solid #EDF2F7", background: "#F7FAFC", whiteSpace: "nowrap", width: col.width || "auto" }}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: "18px", textAlign: "center", color: "#A0AEC0", fontSize: 12 }}>
                  {search ? "No matching records" : "No records found"}
                </td>
              </tr>
            ) : (
              paginated.map((row, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #EDF2F7" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#F7FAFC")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  {columns.map((col) => (
                    <td key={col.key} style={{ padding: "6px 12px", color: "#4A5568", whiteSpace: col.nowrap ? "nowrap" : "normal" }}>
                      {col.render ? col.render(row) : (row[col.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination bar ── */}
      {filtered.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 2px 2px", flexWrap: "wrap", gap: 6 }}>

          {/* Rows per page */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "#718096" }}>Rows</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={{ padding: "3px 6px", border: "1.5px solid #E2E8F0", borderRadius: 6, fontSize: 11, color: "#2D3748", background: "#fff", outline: "none", cursor: "pointer" }}
              onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
              onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
            >
              {[10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          {/* Page info + nav */}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 11, color: "#718096", marginRight: 4 }}>
              {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length}
            </span>
            {[
              { icon: "mdi-chevron-double-left", action: () => setPage(1), disabled: currentPage === 1 },
              { icon: "mdi-chevron-left", action: () => setPage((p) => p - 1), disabled: currentPage === 1 },
              { icon: "mdi-chevron-right", action: () => setPage((p) => p + 1), disabled: currentPage === totalPages },
              { icon: "mdi-chevron-double-right", action: () => setPage(totalPages), disabled: currentPage === totalPages },
            ].map((btn) => (
              <button
                key={btn.icon}
                onClick={btn.action}
                disabled={btn.disabled}
                style={{ width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #E2E8F0", borderRadius: 6, background: btn.disabled ? "#F7FAFC" : "#fff", color: btn.disabled ? "#CBD5E0" : "#4A5568", cursor: btn.disabled ? "not-allowed" : "pointer", fontSize: 14, padding: 0 }}
              >
                <i className={`mdi ${btn.icon}`} />
              </button>
            ))}
          </div>

        </div>
      )}
    </div>
  );
};

/* ── Status card ── */
const StatusCard = ({ icon, title, count, children }) => (
  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", borderRadius: "12px 12px 0 0" }}>
      <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
        <i className={`mdi ${icon}`} />
      </span>
      <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", flex: 1 }}>{title}</span>
      <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 50 }}>
        {count} records
      </span>
    </div>
    <div>{children}</div>
  </div>
);

const withIndex = (rows) => rows.map((r, i) => ({ ...r, _idx: i + 1 }));

const meetingColumns = [
  { key: "_idx", label: "#", width: 36 },
  { key: "vfcName", label: "JFMC Name" },
  { key: "lastMeetingHeld", label: "Meeting Date", nowrap: true, render: (r) => fmtDate(r.lastMeetingHeld) },
  {
    key: "status", label: "Status", width: 76,
    render: (r) => {
      const active = !!r.lastMeetingHeld;
      return <span style={{ background: active ? "#E8F5E9" : "#FFEBEE", color: active ? "#2E7D32" : "#C62828", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 50 }}>{active ? "Active" : "Inactive"}</span>;
    },
  },
];

const auditColumns = [
  { key: "_idx", label: "#", width: 36 },
  { key: "vfcName", label: "JFMC Name" },
  { key: "lastAuditDate", label: "Last Audit", nowrap: true, render: (r) => fmtDate(r.lastAuditDate) },
];

const renewalColumns = [
  { key: "_idx", label: "#", width: 36 },
  { key: "vfcName", label: "JFMC Name" },
  { key: "lastRenewalDate", label: "Last Renewal", nowrap: true, render: (r) => fmtDate(r.lastRenewalDate) },
];

/* ════════════════════════════════════════════════════════
   VFCStatus
════════════════════════════════════════════════════════ */
const VFCStatus = () => {
  const { selectedJfmcType } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [ecMeetings, setEcMeetings] = useState([]);
  const [generalBodyMeetings, setGeneralBodyMeetings] = useState([]);
  const [pendingAudits, setPendingAudits] = useState([]);
  const [pendingRenewals, setPendingRenewals] = useState([]);
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    if (selectedJfmcType) fetchStatusData(selectedJfmcType._id);
  }, [selectedJfmcType]);

  const fetchStatusData = async (jfmcTypeId) => {
    setLoading(true);
    try {
      const config = Authorization();
      const [meetingsRes, auditsRes] = await Promise.all([
        axios.post(`${Api}/api/vfc/pending/meetings`, { jfmcTypeId }, config),
        axios.post(`${Api}/api/audits/pending-audits-renewals`, { jfmcTypeId }, config),
      ]);
      setEcMeetings(meetingsRes.data.ecPendingMeetings || []);
      setGeneralBodyMeetings(meetingsRes.data.generalBodyPendingMeetings || []);
      setPendingAudits(auditsRes.data.pendingAudits || []);
      setPendingRenewals(auditsRes.data.pendingRenewals || []);
    } catch (err) {
      console.error("Error fetching VFC status:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>JFMC Status</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>JFMC</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>VFC Status</span>
        </div>
      </div>

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 16 }}>

          <StatusCard icon="mdi-account-group-outline" title="EC Meetings Pending" count={ecMeetings.length}>
            <StatusTable title="EC Meetings Pending" rows={withIndex(ecMeetings)} columns={meetingColumns} />
          </StatusCard>

          <StatusCard icon="mdi-account-multiple-outline" title="General Body Meetings Pending" count={generalBodyMeetings.length}>
            <StatusTable title="General Body Meetings Pending" rows={withIndex(generalBodyMeetings)} columns={meetingColumns} />
          </StatusCard>

          <StatusCard icon="mdi-clipboard-text-outline" title="Pending Audits" count={pendingAudits.length}>
            <StatusTable title="Pending Audits" rows={withIndex(pendingAudits)} columns={auditColumns} />
          </StatusCard>

          <StatusCard icon="mdi-autorenew" title="Pending Renewals" count={pendingRenewals.length}>
            <StatusTable title="Pending Renewals" rows={withIndex(pendingRenewals)} columns={renewalColumns} />
          </StatusCard>

        </div>
      )}
    </div>
  );
};

export default VFCStatus;
